import { type ComponentType, useCallback, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import {
  isJobActive,
  useCreateScenario,
  useDeleteScenario,
  useScenario,
  useScenarios,
} from "@/api/queries";
import { ErrorPanel, errorText } from "@/components/ErrorPanel";
import { PlanJob_State, type Scenario } from "@/generated/backend/v1/plan";
import { Struct } from "@/generated/google/protobuf/struct";
import { Timestamp } from "@/generated/google/protobuf/timestamp";
import { formatDate, fromJsDate } from "@/plan-logic/lib/dates";
import { PlanPage, usePlanCtx } from "../PlanPage";
import { scenarioRequest } from "@/plan-logic/decisions/changes";
import {
  CarToolForm,
  ExpenseToolForm,
  FinancedToolForm,
  HomeToolForm,
  type ToolFormProps,
} from "./forms";
import { InsuranceToolForm } from "./InsuranceForm";
import { JobToolForm } from "./JobForm";
import { ScenarioResults } from "./ScenarioResults";
import { ScenarioTitle } from "./ScenarioTitle";
import { autoTitle, scenarioTitle } from "@/plan-logic/decisions/title";
import {
  COMING_SOON,
  SCENARIO_TTL_DAYS,
  type ToolInfo,
  type ToolSlug,
  toolByEnum,
  toolBySlug,
  TOOLS,
} from "@/plan-logic/decisions/tools";
import "./decisions.css";
import "@/styles/v47-hub.css";

const FORMS: Record<ToolSlug, ComponentType<ToolFormProps>> = {
  expense: ExpenseToolForm,
  home: HomeToolForm,
  job: JobToolForm,
  car: CarToolForm,
  "financed-purchase": FinancedToolForm,
  insurance: InsuranceToolForm,
};

/** "9 October 2026" (the app's date style). */
const fmtDay = (ts: Timestamp | undefined) =>
  ts ? formatDate(fromJsDate(Timestamp.toDate(ts))) : "";

function inputsOf(s: Scenario | undefined): Record<string, unknown> | undefined {
  if (!s?.inputs) return undefined;
  return Struct.toJson(s.inputs) as Record<string, unknown>;
}

function statusOf(s: Scenario): string {
  if (isJobActive(s.job)) return "Running";
  if (s.job?.state === PlanJob_State.FAILED) return "Didn't finish";
  return "Ready";
}

// ------------------------------------------------------------------ hub

function ScenarioList() {
  const { family } = usePlanCtx();
  const list = useScenarios();
  const del = useDeleteScenario();
  if (list.isPending) return null;
  if (list.isError) return <ErrorPanel error={list.error} onRetry={() => list.refetch()} />;
  const items = list.data;
  return (
    <div className="card pad mt" data-testid="scenario-list">
      <h2 style={{ marginBottom: 4 }}>What you&rsquo;ve tried</h2>
      <p className="mut" style={{ marginBottom: 12 }}>
        Each one is kept for {SCENARIO_TTL_DAYS} days, then deleted. Nothing here changes your plan.
      </p>
      {!items.length && (
        <div className="empty">Nothing yet. Pick a tool above to try something.</div>
      )}
      {items.map((s) => {
        const tool = toolByEnum(s.tool);
        return (
          <div className="dz-srow" key={s.uuid} data-testid="scenario-row">
            <span className="dz-sic" aria-hidden="true">
              {tool?.icon}
            </span>
            <span className="dz-smain">
              <ScenarioTitle scenario={s} family={family} />
              <span className="mut">
                {tool?.title} &middot; tried {fmtDay(s.createdAt)} &middot; kept until{" "}
                {fmtDay(s.expiresAt)}
              </span>
            </span>
            <span className="dz-status">{statusOf(s)}</span>
            {tool && (
              <Link className="mini2" to={`${tool.href}?scenario=${encodeURIComponent(s.uuid)}`}>
                Open
              </Link>
            )}
            <button
              type="button"
              className="linkbtn2 dz-sdel"
              onClick={() => del.mutate(s.uuid)}
              disabled={del.isPending}
              aria-label={`Delete ${scenarioTitle(s, family)}`}
            >
              Delete
            </button>
          </div>
        );
      })}
    </div>
  );
}

function Hub() {
  return (
    <>
      <div className="grid3" id="hub" data-testid="decision-hub">
        {TOOLS.map((t) => (
          <Link
            key={t.slug}
            to={t.href}
            className={`hubc ${t.color}`}
            data-testid={`decision-tool-${t.slug}`}
          >
            <span className="hubi" aria-hidden="true">
              {t.icon}
            </span>
            <b>{t.title}</b>
            <span className="hubd">{t.description}</span>
          </Link>
        ))}
        {COMING_SOON.map((t) => (
          <div className="hubc soon" aria-disabled="true" key={t.title} data-testid="decision-soon">
            <span className="hubi" aria-hidden="true">
              {t.icon}
            </span>
            <span className="hubtag">Coming soon</span>
            <b>{t.title}</b>
            <span className="hubd">{t.description}</span>
            <span className="hubdev">This tool is currently in development</span>
          </div>
        ))}
      </div>
      <ScenarioList />
    </>
  );
}

// ------------------------------------------------------------------ one tool

/**
 * One module, on a page of its own (v42): "Back to Decision Hub", the module's head, then controls on
 * the left and the answer on the right. Nothing runs until Analyze is pressed, and changing an input
 * afterwards puts the answer back behind the button (v43). A step-by-step module (insurance) lays out
 * its own steps and places the answer itself.
 */
function ToolView({ info }: { info: ToolInfo }) {
  const { family, abi } = usePlanCtx();
  const [params, setParams] = useSearchParams();
  const scenarioId = params.get("scenario") ?? undefined;
  const create = useCreateScenario();
  const scenario = useScenario(scenarioId);
  const Form = FORMS[info.slug];
  const opened = scenario.data && scenario.data.tool === info.tool ? scenario.data : undefined;
  const loadingSaved = !!scenarioId && scenario.isPending;
  // Edited since the answer showing was run: the answer waits behind the button again.
  const [edited, setEdited] = useState<string | undefined>(undefined);
  const stale = edited !== undefined && edited === (opened?.uuid ?? "new");
  const onEdit = useCallback(() => setEdited(opened?.uuid ?? "new"), [opened?.uuid]);

  const notFound =
    scenario.isError && (scenario.error as { status?: number } | null)?.status === 404;
  const error = create.isError
    ? errorText(create.error)
    : notFound
      ? `This scenario has expired. They're kept for ${SCENARIO_TTL_DAYS} days.`
      : scenario.isError
        ? errorText(scenario.error)
        : undefined;

  const shown = create.isPending || stale ? undefined : opened;
  const answer = (
    <div className="dz-answer">
      {shown && (
        <div className="dz-rtitle" data-testid="scenario-result-title">
          <ScenarioTitle scenario={shown} family={family} as="h3" />
        </div>
      )}
      <ScenarioResults
        scenario={shown}
        pending={create.isPending}
        error={stale ? undefined : error}
        family={family}
        baseline={abi}
        waiting={info.waiting}
      />
      {shown?.expiresAt && (
        <p className="hint">Kept until {fmtDay(shown.expiresAt)}, then deleted.</p>
      )}
    </div>
  );
  const form = loadingSaved ? (
    <div className="wp-spin" aria-label="Loading" />
  ) : (
    <Form
      key={opened?.uuid ?? "new"}
      busy={create.isPending || isJobActive(opened?.job)}
      defaults={inputsOf(opened)}
      ran={!!shown}
      onEdit={onEdit}
      result={answer}
      onRun={(changes, inputs, comparison) =>
        create.mutate(
          scenarioRequest(
            info.tool,
            changes,
            inputs,
            autoTitle(info.tool, inputs, family),
            comparison,
          ),
          {
            onSuccess: (s) => {
              setEdited(undefined);
              setParams({ scenario: s.uuid });
            },
          },
        )
      }
    />
  );

  return (
    <div data-testid={`decision-${info.slug}`}>
      <Link className="btn g backbtn" to="/app/decisions">
        &#8592; Back to Decision Hub
      </Link>
      <div className="dmod">
        <div className="dmhead">
          <span className="hubi" aria-hidden="true">
            {info.icon}
          </span>
          <div>
            <h2>{info.title}</h2>
            <p>{info.lede}</p>
          </div>
        </div>
        {info.wizard ? (
          <div className="dmwiz">{form}</div>
        ) : (
          <div className="dmbody">
            <div className="dmin">{form}</div>
            <div className="dmout">{answer}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/** /app/decisions (the hub and your scenarios) and /app/decisions/:tool. */
export function DecisionsPage() {
  const { tool } = useParams();
  const info = toolBySlug(tool);
  if (tool && !info) return <Navigate to="/app/decisions" replace />;
  return (
    <PlanPage title="pages.decisions.title" sub="pages.decisions.sub" testId="decisions">
      {() => (info ? <ToolView info={info} /> : <Hub />)}
    </PlanPage>
  );
}
